import { useState } from "react";
import type { ReactNode } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconLock } from "@tabler/icons-react";
import { env } from "@/config/env";
import { useAuth } from "@/auth/useAuth";
import { AdminApiError } from "@/api/client";
import type { AdminActionDef } from "@/api/actions";
import { EnvBadge } from "./EnvBanner";

/**
 * Renders an admin action as a button that, when clicked, opens a confirmation
 * dialog (with a reason field for destructive actions and an extra warning in
 * production), calls the backend endpoint with a fresh ID token, and surfaces
 * the result. On success the Firestore listener reflects the new state — we
 * don't optimistically mutate anything client-side.
 *
 * Disabled (with an explanatory tooltip) when the action is A2-pending,
 * forbidden by design, or the operator lacks the capability.
 */
export function AdminActionButton<P>({
  action,
  params,
  children,
  onDone,
  variant,
}: {
  action: AdminActionDef<P>;
  params: P;
  children?: ReactNode;
  onDone?: (result: unknown) => void;
  variant?: string;
}) {
  const { capabilities, getIdToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isProd = env.isProd;
  // Destructive actions, and the ones that need the same elevation, take superAdmin in production.
  const elevated = action.destructive || action.requiresElevation === true;
  // A few actions need superAdmin in every environment (the backend enforces the same).
  const superAdminMissing = action.requiresSuperAdmin === true && !capabilities.superAdmin;
  const capabilityOk =
    capabilities.act &&
    (!elevated || capabilities.actDestructive) &&
    (!isProd || capabilities.actProd) &&
    !superAdminMissing;

  const enabled = action.availability === "available" && capabilityOk && !!action.run;

  let disabledReason: string | null = null;
  if (action.availability === "a2-pending") disabledReason = action.unavailableNote ?? "Not built yet.";
  else if (action.availability === "forbidden") disabledReason = action.unavailableNote ?? "Not permitted.";
  else if (action.availability === "notApplicable")
    disabledReason = action.unavailableNote ?? "Not applicable in this state.";
  else if (!capabilityOk)
    disabledReason =
      superAdminMissing && capabilities.act
        ? "This action requires the superAdmin role in every environment."
        : isProd && elevated && !capabilities.actDestructive
          ? action.destructive
            ? "Destructive actions in production require the superAdmin role."
            : "This action requires the superAdmin role in production."
          : isProd
            ? "Your role can't perform this action in production."
            : "Your role can't perform this action.";

  const color = action.destructive ? "red" : undefined;

  async function confirm() {
    if (!action.run) return;
    setSubmitting(true);
    try {
      const token = await getIdToken(true);
      const result = await action.run(token, params, reason.trim() || undefined);
      notifications.show({
        color: "teal",
        title: `${action.verb} succeeded`,
        message: action.successMessage?.(result) ?? "The change will appear here in a moment.",
        autoClose: action.successMessage ? 10000 : undefined,
      });
      setOpen(false);
      setReason("");
      onDone?.(result);
    } catch (e) {
      const conflict = e instanceof AdminApiError && e.conflict;
      const label = e instanceof AdminApiError ? action.errorLabel?.(e.code, e.status) : undefined;
      const message =
        e instanceof AdminApiError
          ? `${label ? `${label}: ` : ""}${e.message}${e.status ? ` (HTTP ${e.status}${e.code ? `, ${e.code}` : ""})` : ""}` +
            (e.conflict && e.code === "incorrectVersion"
              ? " The record changed since you opened it: review its latest state before trying again."
              : "")
          : e instanceof Error
            ? e.message
            : String(e);
      notifications.show({
        color: "red",
        title: `${action.verb} failed`,
        message,
        // A 409 explains what blocks the action (an operation, a prompt, a balance): keep it on
        // screen until the operator has read it.
        autoClose: conflict ? false : 8000,
      });
    } finally {
      setSubmitting(false);
    }
  }

  const button = (
    <Button
      size="xs"
      color={color}
      variant={variant ?? (action.destructive ? "light" : "default")}
      disabled={!enabled}
      leftSection={!enabled ? <IconLock size={14} /> : undefined}
      onClick={() => setOpen(true)}
    >
      {children ?? action.verb}
    </Button>
  );

  const reasonMissing = action.requiresReason && reason.trim().length === 0;
  const reasonTooLong =
    action.reasonMaxLength !== undefined && reason.trim().length > action.reasonMaxLength;

  return (
    <>
      {disabledReason ? (
        <Tooltip label={disabledReason} multiline w={320} withArrow>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}

      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title={
          <Group gap="xs">
            {action.destructive ? <IconAlertTriangle size={18} color="var(--mantine-color-red-6)" /> : null}
            <Text fw={700}>Confirm: {action.verb}</Text>
          </Group>
        }
        centered
      >
        <Stack>
          <Text size="sm">{action.description}</Text>

          {isProd ? (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Group gap="xs">
                You are acting in <EnvBadge size="sm" />. This affects real users and real funds.
              </Group>
            </Alert>
          ) : null}

          {action.requiresReason ? (
            <Textarea
              label="Reason (required)"
              description={
                action.reasonMaxLength
                  ? `Recorded for audit — this action is logged against your account. Up to ${action.reasonMaxLength} characters.`
                  : "Recorded for audit — this action is logged against your account."
              }
              placeholder="Why are you taking this action?"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              error={
                reasonTooLong
                  ? `${reason.trim().length} / ${action.reasonMaxLength} characters: shorten the reason.`
                  : undefined
              }
              minRows={2}
              autosize
              required
            />
          ) : null}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              color={color ?? "blue"}
              loading={submitting}
              disabled={reasonMissing || reasonTooLong}
              onClick={confirm}
            >
              {action.verb}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
